import {
  ScheduleType,
  type ScheduledDeliveryState,
  type Schedule,
  type PreDeliveryAction,
  type PostDeliveryAction,
  type RetryConfig,
} from '../shared';
import type { Delivery } from '../Deliveries';
import type { Location } from '../Locations';

export interface ScheduledDelivery {
  name: string;
  uid: string;
  displayName?: string;
  sourceLocation: string;
  sourcePath: string;
  targetLocation: string;
  targetPath: string;
  fileMappings?: Record<string, string>;
  schedule: Schedule;
  preDeliveryAction?: PreDeliveryAction;
  postDeliveryAction?: PostDeliveryAction;
  ignoreEmptySource?: boolean;
  retryConfig?: RetryConfig;
  state: ScheduledDeliveryState;
  nextExecutionTime?: string;
  lastExecutionTime?: string;
  createTime?: string;
  updateTime?: string;
  etag?: string;
}

export interface ScheduledDeliveryDisplay {
  name: string;
  uid: string;
  displayName?: string;
  sourceLocation: Location | null;
  sourcePath: string;
  targetLocation: Location | null;
  targetPath: string;
  fileMappings?: Record<string, string>;
  schedule: Schedule;
  scheduleTypeLabel: 'RRule' | 'Cron';
  scheduleDescription: string;
  preDeliveryAction?: PreDeliveryAction;
  postDeliveryAction?: PostDeliveryAction;
  ignoreEmptySource?: boolean;
  retryConfig?: RetryConfig;
  state: ScheduledDeliveryState;
  nextExecutionTime?: string;
  lastExecutionTime?: string;
  createTime?: string;
  updateTime?: string;
  etag?: string;
}

export const transformScheduledDeliveryToDisplay = (
  scheduledDelivery: ScheduledDelivery,
  locations: Location[],
  formatCron: (expression: string) => string,
): ScheduledDeliveryDisplay => {
  const sourceLocation =
    locations.find(
      (loc) => loc.uid === scheduledDelivery.sourceLocation || loc.name === scheduledDelivery.sourceLocation,
    ) ?? null;
  const targetLocation =
    locations.find(
      (loc) => loc.uid === scheduledDelivery.targetLocation || loc.name === scheduledDelivery.targetLocation,
    ) ?? null;

  const isCron = scheduledDelivery.schedule.type === ScheduleType.SCHEDULE_TYPE_CRON;
  const scheduleTypeLabel: 'RRule' | 'Cron' = isCron ? 'Cron' : 'RRule';

  let scheduleDescription = scheduledDelivery.schedule.expression;
  if (isCron) {
    try {
      scheduleDescription = formatCron(scheduledDelivery.schedule.expression);
    } catch {
      scheduleDescription = scheduledDelivery.schedule.expression;
    }
  }

  return {
    ...scheduledDelivery,
    sourceLocation,
    targetLocation,
    scheduleTypeLabel,
    scheduleDescription,
  };
};

export interface CreateScheduledDeliveryRequest {
  scheduledDelivery: Omit<
    ScheduledDelivery,
    'uid' | 'state' | 'nextExecutionTime' | 'lastExecutionTime' | 'createTime' | 'updateTime'
  >;
  scheduledDeliveryId?: string;
  validateOnly?: boolean;
}

export interface UpdateScheduledDeliveryRequest {
  scheduledDelivery: Partial<ScheduledDelivery> & { name: string };
  updateMask?: string[];
}

export interface ListScheduledDeliveriesParams {
  pageSize?: number;
  pageToken?: string;
  filter?: string;
  orderBy?: string;
}

export interface ListScheduledDeliveriesResponse {
  scheduledDeliveries: ScheduledDelivery[];
  nextPageToken?: string;
  totalSize: number;
}

export interface RunScheduledDeliveryResponse {
  delivery: Delivery;
}
